import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// eslint-disable-next-line import/no-unresolved
import { formatDate, parseDate } from 'of-datepicker'
// eslint-disable-next-line import/no-unresolved
import { useDatePicker } from 'of-datepicker/react'
import Choices from 'choices.js'

const getDescribedBy = (id) => `error_${id} info_${id}`

const getInputClassName = (className) =>
  className ? `js-field-input ${className}` : undefined

let globalScrollLockCount = 0
let locomotiveReadyHandler = null

const positionDatepickerInViewport = (input) => {
  const datepicker = input
    ?.closest('.form-widget__input--date')
    ?.querySelector('.of-datepicker')

  if (!datepicker) {
    return
  }

  datepicker.style.transform = ''
  const { bottom, top } = datepicker.getBoundingClientRect()
  const topOffset = Math.max(0, -top)
  const bottomOffset = Math.max(0, bottom - window.innerHeight)
  const offset = bottomOffset ? bottomOffset : -topOffset

  if (offset) {
    datepicker.style.transform = `translateY(${-offset}px)`
  }
}

const disableGlobalScroll = () => {
  if (globalScrollLockCount === 0) {
    if (window.locomotiveScroll) {
      window.locomotiveScroll.stop()
    } else {
      locomotiveReadyHandler = (event) => {
        if (globalScrollLockCount > 0) {
          event.detail.stop()
        }

        locomotiveReadyHandler = null
      }
      window.addEventListener('locomotive-ready', locomotiveReadyHandler, {
        once: true,
      })
    }
  }

  globalScrollLockCount += 1
}

const enableGlobalScroll = () => {
  if (globalScrollLockCount === 0) {
    return
  }

  globalScrollLockCount -= 1
  if (globalScrollLockCount === 0) {
    window.locomotiveScroll?.start()
    if (locomotiveReadyHandler) {
      window.removeEventListener('locomotive-ready', locomotiveReadyHandler)
      locomotiveReadyHandler = null
    }
  }
}

export const FieldLayout = ({
  id,
  label,
  info,
  error,
  required = false,
  labelClassName,
  widgetClassName = '',
  children,
}) => (
  <div
    className={`js-form-field form-widget form-widget--big js-validate ${widgetClassName}`}
    id={`item_${id}`}
  >
    <div className="form-widget__field">
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
          {required && <>*</>}
        </label>
      )}
      <span
        className={`form-widget__field__info${info ? ' active' : ''}`}
        id={`info_${id}`}
      >
        {info}
      </span>
      {children}
    </div>
    <span
      className={`form-widget__error js-field-error small${
        error ? ' active' : ''
      }`}
      role="alert"
      aria-live="polite"
      id={`error_${id}`}
    >
      {error}
    </span>
  </div>
)

export const TextField = ({
  id,
  name,
  type = 'text',
  value,
  onChange,
  required,
  fieldType = type,
  className,
  inputProps,
  children,
  ...fieldProps
}) => {
  const [isActive, setIsActive] = useState(false)
  const [hasUncontrolledValue, setHasUncontrolledValue] = useState(false)
  const inputRef = useRef(null)
  const {
    onFocus,
    onBlur,
    onChange: onInputChange,
    ref: inputPropsRef,
    ...restInputProps
  } = inputProps || {}
  const supportsNotText = ['text', 'email', 'password', 'tel'].includes(type)
  const isEmpty = value === '' || value === undefined || value === null
  const hasValue = !isEmpty || hasUncontrolledValue
  const labelClassName =
    supportsNotText && !hasValue && !isActive ? 'nottext' : undefined

  const handleFocus = (event) => {
    setIsActive(true)
    setHasUncontrolledValue(Boolean(event.target.value))
    if (onFocus) {
      onFocus(event)
    }
  }

  const handleChange = (event) => {
    setHasUncontrolledValue(Boolean(event.target.value))
    onChange?.(event)
    if (onInputChange && onInputChange !== onChange) {
      onInputChange(event)
    }
  }

  const handleBlur = (event) => {
    setIsActive(false)
    setHasUncontrolledValue(Boolean(event.target.value))
    if (onBlur) {
      onBlur(event)
    }
  }

  return (
    <FieldLayout
      id={id}
      required={required}
      labelClassName={labelClassName}
      {...fieldProps}
    >
      <div
        className={`form-widget__input${
          type === 'password' || fieldType === 'password'
            ? ' form-widget__input--password'
            : ''
        }`}
      >
        <input
          ref={(input) => {
            inputRef.current = input
            if (typeof inputPropsRef === 'function') {
              inputPropsRef(input)
            } else if (inputPropsRef) {
              inputPropsRef.current = input
            }
          }}
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          required={required}
          aria-describedby={getDescribedBy(id)}
          data-required={required ? 'true' : undefined}
          data-type={fieldType}
          className={getInputClassName(className)}
          {...restInputProps}
        />
        {children}
        <span className="line" />
      </div>
    </FieldLayout>
  )
}

export const Input = (props) => <TextField {...props} type="text" />

export const EmailInput = (props) => <TextField {...props} type="email" />

export const PhoneInput = (props) => <TextField {...props} type="tel" />

export const Textarea = ({
  id,
  name,
  value,
  onChange,
  required,
  className,
  textareaProps,
  ...fieldProps
}) => (
  <FieldLayout id={id} required={required} {...fieldProps}>
    <textarea
      id={id}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      aria-describedby={getDescribedBy(id)}
      data-required={required ? 'true' : undefined}
      data-type="textarea"
      className={getInputClassName(className)}
      {...textareaProps}
    />
  </FieldLayout>
)

export const FileInput = ({
  id,
  name,
  onChange,
  required,
  multiple = false,
  accept,
  className,
  inputProps,
  label = 'lub prześlij plik',
  uploadLabel = 'z komputera',
  dropLabel = 'Możesz też przeciągnąć plik tutaj',
  info,
  error,
  reset,
}) => {
  const inputRef = useRef(null)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])

  useEffect(() => {
    if (reset) {
      setSelectedFiles([])
    }
  }, [reset])

  const handleChange = (event) => {
    setSelectedFiles(Array.from(event.target.files || []))
    onChange?.(event)
  }

  const handleFiles = (files) => {
    if (!files?.length || !inputRef.current) {
      return
    }

    const dataTransfer = new DataTransfer()
    Array.from(files).forEach((file) => dataTransfer.items.add(file))
    inputRef.current.files = dataTransfer.files
    handleChange({ target: inputRef.current })
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragging(false)
    }
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setIsDragging(false)
    if (!event.dataTransfer.files.length) {
      return
    }
    handleFiles(multiple ? event.dataTransfer.files : [event.dataTransfer.files[0]])
  }

  return (
    <div
      className="js-form-field form-widget form-widget--big form-widget--file js-validate"
      id={`item_${id}`}
    >
      <div className="form-widget__field">
        <div className="form-widget__file-header">
          {label && <span className="form-widget__file-label">{label}</span>}
          <button
            type="button"
            className="form-widget__file-button"
            onClick={() => inputRef.current?.click()}
          >
            <svg width="21" height="23" viewBox="0 0 21 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 15L19 15" stroke="black" strokeWidth="2"/>
              <path d="M15 11L19 15L15 19" stroke="black" strokeWidth="2"/>
              <path d="M16.9268 21.4062H1V1H9.50638L16.9268 8.33909" stroke="black" strokeWidth="2"/>
              <path d="M9.46094 1.49756V8.46555H16.4289" stroke="black" strokeWidth="2" strokeLinecap="square"/>
            </svg>
            {uploadLabel}
          </button>
        </div>
        <input
          ref={inputRef}
          id={id}
          name={name}
          type="file"
          onChange={handleChange}
          required={required}
          multiple={multiple}
          accept={accept}
          aria-describedby={getDescribedBy(id)}
          data-required={required ? 'true' : undefined}
          data-type="file"
          className={getInputClassName(className || 'visuallyhidden')}
          {...inputProps}
        />
        <div
          className={`form-widget__file-dropzone${
            isDragging ? ' active' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              inputRef.current?.click()
            }
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dropLabel}
        </div>
        {selectedFiles.length > 0 && (
          <span className="form-widget__file-selected" aria-live="polite">
            {selectedFiles.map((file) => file.name).join(', ')}
          </span>
        )}
        <span
          className={`form-widget__field__info${info ? ' active' : ''}`}
          id={`info_${id}`}
        >
          {info}
        </span>
      </div>
      <span
        className={`form-widget__error js-field-error small${
          error ? ' active' : ''
        }`}
        role="alert"
        aria-live="polite"
        id={`error_${id}`}
      >
        {error}
      </span>
    </div>
  )
}

export const PasswordInput = ({ inputProps, ...props }) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  return (
    <TextField
      {...props}
      type={isPasswordVisible ? 'text' : 'password'}
      fieldType="password"
      inputProps={inputProps}
    >
      <button
        type="button"
        className="form-widget__password-toggle"
        aria-label={isPasswordVisible ? 'Ukryj hasło' : 'Pokaż hasło'}
        aria-pressed={isPasswordVisible}
        onClick={() => setIsPasswordVisible((isVisible) => !isVisible)}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.001 5C5.69398 5 2.63398 10.683 2.09098 11.808C2.06195 11.8678 2.04688 11.9335 2.04688 12C2.04688 12.0665 2.06195 12.1322 2.09098 12.192C2.63298 13.317 5.69298 19 12.001 19C18.309 19 21.368 13.317 21.911 12.192C21.94 12.1322 21.9551 12.0665 21.9551 12C21.9551 11.9335 21.94 11.8678 21.911 11.808C21.369 10.683 18.309 5 12.001 5Z"
            stroke="black"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
            stroke="black"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {isPasswordVisible && <path d="m4 4 16 16" />}
        </svg>
      </button>
    </TextField>
  )
}

export const DateInput = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  required,
  className,
  inputProps,
  label,
  info,
  error,
  maxDate, minDate,
}) => {
  const {
    onFocus,
    onBlur: onInputBlur,
    onClick: onInputClick,
    'data-lang': datepickerLanguage,
    ...restInputProps
  } = inputProps || {}
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false)
  const inputClassNames = `${className || ''} ${restInputProps.className || ''}`
  const isEmpty = value === '' || value === undefined || value === null
  const displayValue = value
    ? formatDate(parseDate(value, 'yyyy-mm-dd'), 'dd.mm.yyyy')
    : ''
  const defaultDate = useMemo(() => {
    const today = new Date()
    if (maxDate) return new Date(`${maxDate}T00:00:00`)
    if (minDate && new Date(`${minDate}T00:00:00`) > today) {
      return new Date(`${minDate}T00:00:00`)
    }
    return today
  }, [maxDate, minDate])
  const handleDatepickerOpenChange = useCallback(
    (isOpen) => setIsDatepickerOpen(isOpen),
    []
  )
  const handleChangeDate = useCallback(
    ({ date }) => onChange?.(date ? formatDate(date, 'yyyy-mm-dd') : ''),
    [onChange]
  )
  const datepickerOptions = useMemo(() => {
    const options = {
      lang: datepickerLanguage || 'pl',
      startDate: value ? parseDate(value, 'yyyy-mm-dd') : defaultDate,
      format: 'dd.mm.yyyy',
      enableSelectMonth: true,
      enableSelectYear: true,
      enableSelectButton: true,
      selectCheckmark: '<svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path d="M0.707092 3.70703L3.70709 6.70703L9.70709 0.707031" stroke="#1D1D1D" stroke-width="2"/>\n' +
        '</svg>',
      onChangeOpen: handleDatepickerOpenChange,
      selectArrow: '<svg width="24" height="16" viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
        '<path fill-rule="evenodd" clip-rule="evenodd" d="M12 8.78927L3.21073 -1.40345e-07L1.40345e-07 3.21073L12 15.2107L24 3.21072L20.7893 -6.57207e-07L12 8.78927Z" fill="white"/>\n' +
        '</svg>',
      onChangeDate: handleChangeDate,
    }

    if (inputClassNames.includes('js-date-birth')) {
      options.lastActiveDate = new Date()
      options.firstActiveDate = new Date('1930-01-01')
    }

    if (maxDate) {
      options.lastActiveDate = new Date(`${maxDate}T00:00:00`)
    }

    if (minDate) {
      options.firstActiveDate = new Date(`${minDate}T00:00:00`)
    }

    return options
  }, [
    handleChangeDate,
    handleDatepickerOpenChange,
    datepickerLanguage,
    defaultDate,
    inputClassNames,
    maxDate,
    minDate,
    value,
  ])

  const { inputRef, datepicker: datepickerRef } = useDatePicker(datepickerOptions)

  useEffect(() => {
    const input = inputRef.current
    if (!input) {
      return undefined
    }

    const positionDatepicker = () => {
      window.requestAnimationFrame(() => positionDatepickerInViewport(input))
    }

    // Safari nie ustawia fokusu na przycisku po kliknięciu, więc focusout ma
    // relatedTarget === null i datepicker zamyka się przed zdarzeniem click
    // (np. przycisk "Zatwierdź" nie działa). Blokujemy zmianę fokusu na mousedown.
    const calendar = input
      .closest('.form-widget__input--date')
      ?.querySelector('.of-datepicker')
    const preventButtonFocusChange = (event) => {
      if (event.target.closest('button')) {
        event.preventDefault()
      }
    }

    input.addEventListener('focus', positionDatepicker)
    window.addEventListener('resize', positionDatepicker)
    calendar?.addEventListener('mousedown', preventButtonFocusChange)

    return () => {
      input.removeEventListener('focus', positionDatepicker)
      window.removeEventListener('resize', positionDatepicker)
      calendar?.removeEventListener('mousedown', preventButtonFocusChange)
    }
  }, [inputRef])

  useEffect(() => {
    const datepicker = datepickerRef.current
    const selectedDate = datepicker?.getCalendarData().activeDate
    const valueDate = value ? parseDate(value, 'yyyy-mm-dd') : null

    if (datepicker && selectedDate?.getTime() !== valueDate?.getTime()) {
      if (valueDate) {
        datepicker.setDate(valueDate)
      } else {
        datepicker.setCalendarActiveDate(null)
        datepicker.setCalendarFocusedDate(defaultDate)
        datepicker.setCalendarActiveMonth(
          new Date(defaultDate.getFullYear(), defaultDate.getMonth(), 1)
        )
        datepicker.generateCalendarDays()
        datepicker.setSelectedDate()
        datepicker.setInputValue()
      }
    }
  }, [defaultDate, displayValue, value])

  const handleBlur = (event) => {
    onInputBlur?.(event)
    onBlur?.(event)
  }

  const handleClick = (event) => {
    onInputClick?.(event)
    event.stopPropagation()
  }

  return (
    <div
      className="js-form-field form-widget form-widget--big js-validate"
      id={`item_${id}`}
    >
      <div className="form-widget__field">
        <div className="form-widget__input form-widget__input--date">
          <svg
            className="form-widget__date-icon"
            width="24"
            height="26"
            viewBox="0 0 24 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path d="M22 3H2C1.44772 3 1 3.44772 1 4V24C1 24.5523 1.44772 25 2 25H22C22.5523 25 23 24.5523 23 24V4C23 3.44772 22.5523 3 22 3Z" />
            <path d="M18 1V5" />
            <path d="M6 1V5" />
            <path d="M1 9H23" />
            <path d="M7.5 13.9995H11L9 16.4995C9.32885 16.4995 9.65263 16.5806 9.94266 16.7356C10.2327 16.8906 10.48 17.1147 10.6628 17.3881C10.8455 17.6615 10.958 17.9758 10.9903 18.303C11.0226 18.6303 10.9737 18.9605 10.848 19.2643C10.7222 19.5682 10.5235 19.8363 10.2694 20.045C10.0153 20.2538 9.71358 20.3966 9.39108 20.4609C9.06857 20.5252 8.73519 20.509 8.42045 20.4137C8.10571 20.3184 7.81932 20.147 7.58665 19.9146" />
            <path d="M14 15.4995L16 13.9995V20.4995" />
          </svg>
          {label && (
            <label htmlFor={id} className={isEmpty ? 'nottext' : undefined}>
              {label}
              {required && <>*</>}
            </label>
          )}
          <input
            ref={inputRef}
            id={id}
            name={name}
            type="text"
            value={displayValue}
            onFocus={onFocus}
            onBlur={handleBlur}
            onClick={handleClick}
            readOnly
            required={required}
            aria-describedby={getDescribedBy(id)}
            data-required={required ? 'true' : undefined}
            data-type="date"
            className={getInputClassName(className)}
            {...restInputProps}
          />
          <button
            type="button"
            className={`form-widget__date-toggle ${isDatepickerOpen ? 'open' : ''}`}
            aria-label="Otwórz kalendarz"
            aria-expanded={isDatepickerOpen}
          >
            <svg
              width="24"
              height="16"
              viewBox="0 0 24 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 8.78927L20.7893 -1.40345e-07L24 3.21073L12 15.2107L-1.40345e-07 3.21072L3.21073 -6.57207e-07L12 8.78927Z"
              />
            </svg>
          </button>
          <span className="line" />
        </div>
        <span
          className={`form-widget__field__info${info ? ' active' : ''}`}
          id={`info_${id}`}
        >
          {info}
        </span>
      </div>
      <span
        className={`form-widget__error js-field-error small${
          error ? ' active' : ''
        }`}
        role="alert"
        aria-live="polite"
        id={`error_${id}`}
      >
        {error}
      </span>
    </div>
  )
}

export const Checkbox = ({
  id,
  name,
  value,
  checked,
  onChange,
  required,
  className,
  inputProps,
  label,
  labelUrl,
  labelUrlText,
  info,
  error,
}) => (
  <div className={`widget-checkbox js-validate`}>
    <input
      id={id}
      name={name}
      type="checkbox"
      value={value}
      checked={checked}
      onChange={onChange}
      required={required}
      aria-describedby={getDescribedBy(id)}
      data-required={required ? 'true' : undefined}
      data-type="checkbox"
      className={getInputClassName(className || 'visuallyhidden')}
      {...inputProps}
    />
    {label && (
      <label htmlFor={id}>
        {required && <sup>*</sup>}
        {label}{' '}
        {labelUrl && (
          <a href={labelUrl} target="_blank" rel="noopener noreferrer">
            {labelUrlText}
          </a>
        )}
      </label>
    )}
    {info && (
      <span className="form-widget__field__info active" id={`info_${id}`}>
        {info}
      </span>
    )}
    <span
      className={`form-widget__error js-field-error small${
        error ? ' active' : ''
      }`}
      role="alert"
      aria-live="polite"
      id={`error_${id}`}
    >
      {error}
    </span>
  </div>
)

export const RadioGroup = ({
  id,
  name,
  options,
  value,
  onChange,
  required,
  className,
  inputProps,
  ...fieldProps
}) => (
  <FieldLayout id={id} required={required} {...fieldProps}>
    <div
      role="radiogroup"
      aria-describedby={getDescribedBy(id)}
      aria-required={required || undefined}
    >
      {options.map((option) => {
        const optionId = option.id || `${id}_${option.value}`

        return (
          <div className="widget-checkbox" key={option.value}>
            <input
              id={optionId}
              name={name}
              type="radio"
              value={option.value}
              checked={value === option.value}
              onChange={onChange}
              required={required}
              data-type="radio"
              className={getInputClassName(
                option.className || className || 'visuallyhidden'
              )}
              disabled={option.disabled}
              {...inputProps}
            />
            <label htmlFor={optionId}>{option.label}</label>
          </div>
        )
      })}
    </div>
  </FieldLayout>
)

export const Select = ({
  id,
  name,
  options,
  value,
  onChange,
  required,
  multiple = false,
  placeholder,
  className,
  selectProps,
  selectedLabel,
  label,
  info,
  error,
  widgetClassName = '',
  classNameLabel = '',
}) => {
  const selectRef = useRef(null)
  const choicesRef = useRef(null)
  const isDropdownOpenRef = useRef(false)
  const [isActive, setIsActive] = useState(false)
  const { onFocus, onBlur, ...restSelectProps } = selectProps || {}
  const isEmpty = multiple ? !value?.length : !value
  const labelClassName = isEmpty && !isActive ? 'nottext' : undefined

  useEffect(() => {
    const select = selectRef.current
    if (!select) {
      return undefined
    }

    const handleShowDropdown = () => {
      if (isDropdownOpenRef.current) {
        return
      }

      isDropdownOpenRef.current = true
      disableGlobalScroll()
    }

    const handleHideDropdown = () => {
      if (!isDropdownOpenRef.current) {
        return
      }

      isDropdownOpenRef.current = false
      enableGlobalScroll()
    }

    const handleDropdownScroll = (event) => {
      event.stopPropagation()
    }

    choicesRef.current = new Choices(select, {
      searchEnabled: true,
      itemSelectText: '',
      shouldSort: false,
      placeholder: placeholder !== undefined,
      placeholderValue: placeholder,
    })
    const dropdown = select
      .closest('.choices')
      ?.querySelector('.choices__list--dropdown')
    select.addEventListener('showDropdown', handleShowDropdown)
    select.addEventListener('hideDropdown', handleHideDropdown)
    dropdown?.addEventListener('wheel', handleDropdownScroll)
    dropdown?.addEventListener('touchmove', handleDropdownScroll)

    return () => {
      select.removeEventListener('showDropdown', handleShowDropdown)
      select.removeEventListener('hideDropdown', handleHideDropdown)
      dropdown?.removeEventListener('wheel', handleDropdownScroll)
      dropdown?.removeEventListener('touchmove', handleDropdownScroll)
      handleHideDropdown()
      choicesRef.current?.destroy()
      choicesRef.current = null
    }
  }, [])

  useEffect(() => {
    const choices = choicesRef.current
    if (!choices) {
      return
    }

    choices.setChoices(options, 'value', 'label', true)
    if (value && (!multiple || value.length)) {
      choices.setChoiceByValue(value)
    }
  }, [options, value, multiple])

  useEffect(() => {
    const choices = choicesRef.current
    if (!choices) {
      return
    }

    choices.removeActiveItems()
    if (value && (!multiple || value.length)) {
      choices.setChoiceByValue(value)
    }
  }, [value])

  const handleFocus = (event) => {
    setIsActive(true)
    onFocus?.(event)
  }

  const handleBlur = (event) => {
    setIsActive(false)
    onBlur?.(event)
  }

  return (
    <div
      className={`js-form-field form-widget form-widget--big js-validate ${widgetClassName}`}
      id={`item_${id}`}
    >
      <div className="form-widget__field">
        {label && (
          <label htmlFor={id} className={`${labelClassName} ${classNameLabel}`}>
            {label}
            {required && <>*</>}
          </label>
        )}
        <div
          className="form-widget__input form-widget__input--select"
          data-selected-label={selectedLabel}
        >
          <select
            ref={selectRef}
            id={id}
            name={name}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            required={required}
            multiple={multiple}
            aria-describedby={getDescribedBy(id)}
            data-required={required ? 'true' : undefined}
            data-type="select"
            className={getInputClassName(className)}
            {...restSelectProps}
          />
          <svg
            className="form-widget__select-arrow"
            width="24"
            height="16"
            viewBox="0 0 24 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12 8.78927L20.7893 -1.40345e-07L24 3.21073L12 15.2107L-1.40345e-07 3.21072L3.21073 -6.57207e-07L12 8.78927Z"
            />
          </svg>
          <span className="line" />
        </div>
        <span
          className={`form-widget__field__info${info ? ' active' : ''}`}
          id={`info_${id}`}
        >
          {info}
        </span>
      </div>
      <span
        className={`form-widget__error js-field-error small${
          error ? ' active' : ''
        }`}
        role="alert"
        aria-live="polite"
        id={`error_${id}`}
      >
        {error}
      </span>
    </div>
  )
}

export const FormWidgets = {
  Input,
  EmailInput,
  PhoneInput,
  Textarea,
  FileInput,
  PasswordInput,
  Checkbox,
  RadioGroup,
  DateInput,
  Select,
}
